import React from 'react';
import {
  Dialog,
  Box,
  InputBase,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Search,
  ArrowForward,
  AddCircleOutline,
  DescriptionOutlined,
  KeyboardReturn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Command, staticResolver, recordResolver, resolveAll } from './resolver';
import { useAppTheme } from '../theme';

const ICONS: Record<string, React.ElementType> = {
  navigate: ArrowForward,
  create: AddCircleOutline,
  record: DescriptionOutlined,
  action: ArrowForward,
  ai: Search,
};

/**
 * Ctrl/Cmd+K command palette.
 *
 * Static commands resolve synchronously so the list never feels laggy; record
 * search is debounced and merged in when it arrives. The two are deliberately
 * separate resolvers — a slow network must never delay "type i, hit enter, go
 * to Invoices".
 */
interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * Text to pre-fill when the palette opens, for callers that already have the
   * user's question — Mission Control's AI panel hands over its suggested or
   * typed question this way, so the palette stays the single place a
   * natural-language query is resolved instead of growing a second resolver.
   */
  initialQuery?: string;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, initialQuery }) => {
  const navigate = useNavigate();
  const { colors } = useAppTheme();

  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<Command[]>([]);
  const [active, setActive] = React.useState(0);
  const [searching, setSearching] = React.useState(false);
  const listRef = React.useRef<HTMLUListElement>(null);

  // Instant pass — static only, no await on the network.
  React.useEffect(() => {
    let cancelled = false;
    staticResolver.resolve(query).then((r) => {
      if (!cancelled) {
        setResults(r);
        setActive(0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [query]);

  // Debounced pass — merges record hits in behind the static ones.
  React.useEffect(() => {
    if (query.trim().length < 3) {
      setSearching(false);
      return;
    }
    setSearching(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const merged = await resolveAll(query, [staticResolver, recordResolver], controller.signal);
      if (!controller.signal.aborted) {
        setResults(merged);
        setSearching(false);
      }
    }, 220);

    return () => {
      clearTimeout(timer);
      controller.abort();
      setSearching(false);
    };
  }, [query]);

  // Seed on each open, not on mount: the palette stays mounted between opens,
  // so keying off `initialQuery` alone would leave a stale question in the box
  // the next time it is opened from the keyboard with nothing to seed.
  React.useEffect(() => {
    if (open) {
      setQuery(initialQuery ?? '');
      setActive(0);
    }
  }, [open, initialQuery]);

  const run = React.useCallback(
    (cmd: Command) => {
      if (cmd.path) navigate(cmd.path);
      onClose();
    },
    [navigate, onClose]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[active]) run(results[active]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Keep the highlighted row in view when arrowing past the fold.
  React.useEffect(() => {
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  let lastGroup = '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden', mt: '10vh', alignSelf: 'flex-start' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.75, borderBottom: 1, borderColor: 'divider' }}>
        <Search sx={{ color: 'text.disabled' }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search or jump to…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          sx={{ fontSize: '1rem' }}
        />
        {searching && <CircularProgress size={15} />}
        <Chip label="ESC" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.625rem' }} />
      </Box>

      <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
        {results.length === 0 && (
          <Box sx={{ py: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {query ? `Nothing matches “${query}”` : 'Type to search'}
            </Typography>
          </Box>
        )}

        <List ref={listRef} dense disablePadding>
          {results.map((cmd, i) => {
            const Icon = ICONS[cmd.kind] || ArrowForward;
            const showGroup = cmd.group !== lastGroup;
            lastGroup = cmd.group;

            return (
              <React.Fragment key={cmd.id}>
                {showGroup && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      px: 2,
                      pt: 1.5,
                      pb: 0.5,
                      color: 'text.disabled',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontSize: '0.625rem',
                    }}
                  >
                    {cmd.group}
                  </Typography>
                )}
                <ListItemButton
                  selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(cmd)}
                  sx={{
                    px: 2,
                    py: 0.875,
                    '&.Mui-selected': { backgroundColor: `${colors.primary}14` },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon sx={{ fontSize: 17, color: i === active ? colors.primary : 'text.disabled' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={cmd.label}
                    secondary={cmd.hint}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: i === active ? 600 : 400 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                  {i === active && <KeyboardReturn sx={{ fontSize: 15, color: 'text.disabled' }} />}
                </ListItemButton>
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Dialog>
  );
};

/** Global Ctrl/Cmd+K binding. */
export const useCommandPalette = () => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return { open, setOpen };
};

export default CommandPalette;
